import React, { useMemo, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import ChevronIcon from '../../assets/svgs/ChevronIcon';

import { idTypes, optionShape } from '../../utils/propTypesShapes';

import { SelectOptionsStyle } from './SelectOptionsStyle';

const SelectOptions = ({ 
  error, 
  options, 
  shape, 
  theme, 
  value, 
  onChange, 
  isSearchable 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (isOpen && isSearchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, isSearchable]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [containerRef]);

  const classNames = useMemo(() => {
    const current = [];

    if (theme?.length) {
      current.push(`${theme}-theme`);
    }

    if (shape?.length) {
      current.push(`${shape}-theme`);
    }

    if (error?.length) {
      current.push('with-error');
    }
    
    if (isOpen) {
      current.push('is-open');
    }

    if (isSearchable) {
      current.push('is-searchable');
    }

    return current;
  }, [error?.length, shape, theme, isOpen, isSearchable]);

  const selectedOptionText = useMemo(() => {
    const currentOption = options.find((option) => option?.value === value);
    return currentOption?.text?.length ? currentOption.text : '';
  }, [options, value]);

  const filteredOptions = useMemo(() => {
    if (!isSearchable || !searchTerm) {
      return options;
    }

    const lowerCaseSearchTerm = searchTerm.toLowerCase();

    return options.filter((option) => 
      option?.text?.toLowerCase().includes(lowerCaseSearchTerm)
    );
  }, [options, isSearchable, searchTerm]);
  
  const handleOptionClick = (newValue) => {
    onChange(newValue);
    setIsOpen(false);
    setSearchTerm(''); 
  };

  return (
    <SelectOptionsStyle className={classNames} ref={containerRef}>
      <div 
        className="select-container" 
        onClick={() => {
            if (!isOpen) {
                setIsOpen(true);
            }
        }}
      >
        
        {isSearchable && isOpen ? (
            <div className="select-header">
                <input
                    ref={searchInputRef}
                    type="text"
                    className="select-search-input"
                    placeholder="Pesquisar..."
                    value={searchTerm}
                    onChange={(event) => {
                        setSearchTerm(event.target.value);
                    }}
                    onClick={(e) => e.stopPropagation()} 
                />
            </div>
        ) : (
            <div className="select-header">
                <div className="selected-name">{selectedOptionText}</div>
            </div>
        )}
        
        <select
          className="select-element"
          value={value}
          onChange={(event) => onChange(event.target.value)}
        >
          {options.map((option) => {
            if (option) {
              return (
                <option key={option.key} value={option.value}>
                  {option.text}
                </option>
              );
            }

            return null;
          })}
        </select>
        
        <span className="icon" onClick={() => setIsOpen((prev) => !prev)}>
          <ChevronIcon />
        </span>
        
        {isOpen && (
            <div className="select-dropdown-menu">
                <ul className="select-options-list">
                    {filteredOptions.map((option) => (
                        <li
                            key={option.key}
                            className={`option-item ${option.value === value ? 'selected' : ''}`}
                            onClick={(e) => {
                                e.stopPropagation(); 
                                handleOptionClick(option.value);
                            }}
                        >
                            {option.text}
                        </li>
                    ))}
                    {!filteredOptions.length && (
                        <li className="option-item no-results">Nenhum resultado encontrado.</li>
                    )}
                </ul>
            </div>
        )}
      </div>
      {error?.length ? <p className="select-error">{error}</p> : null}
    </SelectOptionsStyle>
  );
};

SelectOptions.propTypes = {
  error: PropTypes.string,
  options: PropTypes.arrayOf(optionShape),
  shape: PropTypes.string,
  theme: PropTypes.string,
  value: PropTypes.oneOfType([idTypes]).isRequired,
  onChange: PropTypes.func.isRequired,
  isSearchable: PropTypes.bool,
};

SelectOptions.defaultProps = {
  theme: 'default',
  shape: 'rounded',
  error: '',
  options: [],
  isSearchable: false,
};

export default SelectOptions;